import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Building2, Mail, Phone, MapPin, User,
  CreditCard, FileText
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MobileCustomerCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [customer, setCustomer] = useState({
    name: '',
    org_number: '',
    vat_number: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Sverige',
    contact_person: '',
    payment_terms: 30
  });

  const lookupCompany = async () => {
    if (!customer.org_number || customer.org_number.length < 10) return;
    
    setLookingUp(true);
    try {
      const response = await fetch(`${API_BASE}/api/company-lookup/${customer.org_number}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomer(prev => ({
          ...prev,
          name: data.name || prev.name,
          address: data.address || prev.address,
          postal_code: data.postal_code || prev.postal_code,
          city: data.city || prev.city,
          vat_number: data.vat_number || prev.vat_number
        }));
      }
    } catch (err) {
      console.error('Lookup error:', err);
    } finally {
      setLookingUp(false);
    }
  };

  const handleSave = async () => {
    if (!customer.name) {
      alert('Ange företagsnamn');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customer)
      });

      if (response.ok) {
        navigate('/m/customers');
      } else {
        const error = await response.json();
        alert(error.detail || 'Kunde inte spara kund');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Fel vid sparande');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m/customers')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('customers.newCustomer')}</div>
        </div>
        <button 
          className="mobile-header-btn" 
          onClick={handleSave}
          disabled={saving}
          style={{ background: 'rgba(255,255,255,0.25)' }}
        >
          <Save size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="mobile-content">
        {/* Company Info */}
        <div className="mobile-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Building2 size={18} color="#1e3a5f" />
            <span style={{ fontWeight: 600 }}>Företagsinformation</span>
          </div>
          
          <div className="mobile-input-group">
            <label className="mobile-label">{t('customers.orgNumber')}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className="mobile-input"
                placeholder="XXXXXX-XXXX"
                value={customer.org_number}
                onChange={(e) => setCustomer(prev => ({ ...prev, org_number: e.target.value }))}
                style={{ flex: 1 }}
              />
              <button
                onClick={lookupCompany}
                disabled={lookingUp || !customer.org_number}
                style={{
                  padding: '0 16px',
                  background: '#1e3a5f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  opacity: lookingUp || !customer.org_number ? 0.5 : 1
                }}
              >
                {lookingUp ? '...' : t('customers.lookup')}
              </button>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              {t('customers.autoLookup')}
            </div>
          </div>
          
          <div className="mobile-input-group">
            <label className="mobile-label">{t('customers.name')} *</label>
            <input
              type="text"
              className="mobile-input"
              placeholder="Företag AB"
              value={customer.name}
              onChange={(e) => setCustomer(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          
          <div className="mobile-input-group" style={{ marginBottom: 0 }}>
            <label className="mobile-label">{t('customers.vatNumber')}</label>
            <input
              type="text"
              className="mobile-input"
              placeholder="SE123456789001"
              value={customer.vat_number}
              onChange={(e) => setCustomer(prev => ({ ...prev, vat_number: e.target.value }))}
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className="mobile-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <User size={18} color="#1e3a5f" />
            <span style={{ fontWeight: 600 }}>Kontaktuppgifter</span>
          </div>
          
          <div className="mobile-input-group">
            <label className="mobile-label">{t('customers.contactPerson')}</label>
            <input
              type="text"
              className="mobile-input"
              placeholder="Anna Andersson"
              value={customer.contact_person}
              onChange={(e) => setCustomer(prev => ({ ...prev, contact_person: e.target.value }))}
            />
          </div>
          
          <div className="mobile-input-group">
            <label className="mobile-label">{t('customers.email')}</label>
            <input
              type="email"
              className="mobile-input"
              placeholder="info@foretag.se"
              value={customer.email}
              onChange={(e) => setCustomer(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
          
          <div className="mobile-input-group" style={{ marginBottom: 0 }}>
            <label className="mobile-label">{t('customers.phone')}</label>
            <input
              type="tel"
              className="mobile-input"
              placeholder="08-123 456 78"
              value={customer.phone}
              onChange={(e) => setCustomer(prev => ({ ...prev, phone: e.target.value }))}
            />
          </div>
        </div>

        {/* Address */}
        <div className="mobile-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <MapPin size={18} color="#1e3a5f" />
            <span style={{ fontWeight: 600 }}>Adress</span>
          </div>
          
          <div className="mobile-input-group">
            <label className="mobile-label">{t('customers.address')}</label>
            <input
              type="text"
              className="mobile-input"
              placeholder="Storgatan 1"
              value={customer.address}
              onChange={(e) => setCustomer(prev => ({ ...prev, address: e.target.value }))}
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
            <div className="mobile-input-group" style={{ marginBottom: 0 }}>
              <label className="mobile-label">{t('customers.postalCode')}</label>
              <input
                type="text"
                className="mobile-input"
                placeholder="123 45"
                value={customer.postal_code}
                onChange={(e) => setCustomer(prev => ({ ...prev, postal_code: e.target.value }))}
              />
            </div>
            <div className="mobile-input-group" style={{ marginBottom: 0 }}>
              <label className="mobile-label">{t('customers.city')}</label>
              <input
                type="text"
                className="mobile-input"
                placeholder="Stockholm"
                value={customer.city}
                onChange={(e) => setCustomer(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="mobile-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <CreditCard size={18} color="#1e3a5f" />
            <span style={{ fontWeight: 600 }}>Betalningsvillkor</span>
          </div>
          
          <div className="mobile-input-group" style={{ marginBottom: 0 }}>
            <label className="mobile-label">{t('customers.paymentTerms')}</label>
            <select
              className="mobile-input"
              value={customer.payment_terms}
              onChange={(e) => setCustomer(prev => ({ ...prev, payment_terms: parseInt(e.target.value) }))}
              style={{ padding: '12px' }}
            >
              <option value="10">10 {t('customers.days')}</option>
              <option value="15">15 {t('customers.days')}</option>
              <option value="20">20 {t('customers.days')}</option>
              <option value="30">30 {t('customers.days')}</option>
              <option value="45">45 {t('customers.days')}</option>
              <option value="60">60 {t('customers.days')}</option>
            </select>
          </div>
        </div>

        {/* Save Button */}
        <button 
          className="mobile-btn mobile-btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ marginTop: '8px' }}
        >
          {saving ? (
            <div className="mobile-spinner" style={{ width: '18px', height: '18px', borderTopColor: 'white' }} />
          ) : (
            <>
              <Save size={18} />
              {t('common.save')}
            </>
          )}
        </button>
      </main>
    </div>
  );
}
